import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ClipboardList,
  Eye,
  LogIn,
  CreditCard,
  Settings,
  MessageSquare,
  CalendarCheck,
  Pill,
  Activity,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface AuditEntry {
  id: string;
  action: string;
  resource_type: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
}

const ACTION_CONFIG: Record<string, { icon: React.ElementType; label: string }> = {
  view_assessment: { icon: Eye, label: "Viewed assessment" },
  create_assessment: { icon: ClipboardList, label: "Submitted assessment" },
  update_assessment: { icon: ClipboardList, label: "Updated assessment" },
  view_prescription: { icon: Pill, label: "Viewed prescription" },
  view_appointment: { icon: CalendarCheck, label: "Viewed appointment" },
  view_appointments: { icon: CalendarCheck, label: "Viewed appointments" },
  book_appointment: { icon: CalendarCheck, label: "Booked appointment" },
  send_message: { icon: MessageSquare, label: "Sent a message" },
  view_messages: { icon: MessageSquare, label: "Viewed messages" },
  update_profile: { icon: Settings, label: "Updated profile" },
  view_billing: { icon: CreditCard, label: "Viewed billing" },
  sign_in: { icon: LogIn, label: "Signed in" },
};

function getConfig(action: string) {
  return ACTION_CONFIG[action] ?? { icon: Activity, label: action.replace(/_/g, " ") };
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function ActivityHistory() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("audit_logs")
        .select("id, action, resource_type, created_at, metadata")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);
      setEntries((data as AuditEntry[]) ?? []);
      setLoading(false);
    };
    fetch();
  }, [user]);

  if (loading) return null;
  if (entries.length === 0) return null;

  const visible = expanded ? entries : entries.slice(0, 5);

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg text-foreground">Activity History</h2>
        <span className="text-xs text-muted-foreground">{entries.length} events</span>
      </div>
      <Card className="border-border bg-card">
        <CardContent className="p-0">
          <ScrollArea className={expanded && entries.length > 8 ? "max-h-[400px]" : undefined}>
            <ul className="divide-y divide-border">
              {visible.map((entry) => {
                const { icon: Icon, label } = getConfig(entry.action);
                return (
                  <li key={entry.id} className="flex items-center gap-3 px-4 py-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Icon size={14} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground truncate">{label}</p>
                      <p className="text-xs text-muted-foreground capitalize">{entry.resource_type.replace(/_/g, " ")}</p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(entry.created_at)}</span>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
          {entries.length > 5 && (
            <div className="border-t border-border px-4 py-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground gap-1"
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? (
                  <>Show less <ChevronUp size={14} /></>
                ) : (
                  <>Show all {entries.length} events <ChevronDown size={14} /></>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
